import { useState, useEffect } from "react";
import {
  Home,
  BarChart2,
  Bell,
  UserPlus,
  Users,
  Briefcase,
  DollarSign,
  Mail,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./SideMenu.css";

export default function AdminSideMenu() {
  const [active, setActive] = useState("home");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // ✅ Unified .env logic (works for both Vite and CRA)
  const API_BASE_URL =
    import.meta.env?.VITE_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "http://localhost:5000/api";

  // ✅ Fetch logged-in user info
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (!storedUser?.id) return;

        const response = await fetch(`${API_BASE_URL}/users/${storedUser.id}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok)
          throw new Error(`❌ ${response.status} ${response.statusText}`);
        const data = await response.json();
        setUser(data);
      } catch (err) {
        console.error("❌ Failed to load user info:", err);
      }
    };
    fetchUser();
  }, [API_BASE_URL]);

  const menuItems = [
    {
      id: "home",
      icon: <Home size={18} />,
      label: "Dashboard",
      path: "/admin/dashboard",
    },
    {
      id: "employees",
      icon: <Users size={18} />,
      label: "All Employees",
      path: "/admin/employees",
    },
    {
      id: "clients",
      icon: <Briefcase size={18} />,
      label: "Clients",
      path: "/admin/clients",
    },
    {
      id: "projects",
      icon: <BarChart2 size={18} />,
      label: "Projects",
      path: "/admin/projects",
    },
    {
      id: "emails",
      icon: <Mail size={18} />,
      label: "Emails",
      path: "/admin/emails",
    },
    {
      id: "notifications",
      icon: <Bell size={18} />,
      label: "Notifications",
      path: "/admin/notifications",
    },
    {
      id: "payments",
      icon: <DollarSign size={18} />,
      label: "Payments",
      path: "/admin/payments",
    },
    {
      id: "add-manager",
      icon: <UserPlus size={18} />,
      label: "Add Manager",
      path: "/admin/add-manager",
    },
  ];

  const handleClick = (item) => {
    setActive(item.id);
    if (item.path) navigate(item.path);
  };

  return (
    <aside className="sm_container">
      <div className="sm_brand">
        <div className="sm_logo">
          <img
            src="/assets/nam-mark.png"
            alt="Nam-Mark Logo"
            className="sm_logo_img"
          />
        </div>
      </div>

      <nav className="sm_nav" aria-label="Main navigation">
        {menuItems.map((item) => (
          <motion.button
            key={item.id}
            type="button"
            onClick={() => handleClick(item)}
            className={`sm_item ${active === item.id ? "sm_item_active" : ""}`}
            aria-current={active === item.id ? "page" : undefined}
            aria-label={item.label}
            whileHover={{ translateX: 4 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
          >
            <div className="sm_item_icon">{item.icon}</div>
            <div className="sm_item_label">{item.label}</div>
            {active === item.id && (
              <motion.span
                layoutId="sm_active_indicator"
                className="sm_active_indicator"
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                aria-hidden
              />
            )}
          </motion.button>
        ))}
      </nav>

      <div className="sm_footer">
        <div className="sm_profile">
          <div className="sm_avatar" aria-hidden>
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt="User Avatar"
                className="sm_avatar_img"
              />
            ) : (
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <rect
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                  rx="6"
                  fill="#eef2ff"
                />
                <circle cx="12" cy="9" r="3" fill="#c7d2fe" />
              </svg>
            )}
          </div>
          <div className="sm_profile_meta">
            <div className="sm_profile_name">{user?.name || "Loading..."}</div>
            <div className="sm_profile_role">
              {user?.role || "Fetching role..."}
            </div>
          </div>
        </div>

        <button
          className="sm_upgrade_btn"
          type="button"
          onClick={() => {
            localStorage.removeItem("token"); // clear token
            localStorage.removeItem("user"); // optional, if you store user info
            window.location.href = "/"; // navigate to root page
          }}
        >
          Logout
        </button>
      </div>
    </aside>
  );
}
